/**
 * /pressure-testing/invoices (PT-6, fieldiq-api#939). Two panels:
 *
 *  READY TO INVOICE — `GET /pressure-testing/invoices/billable`: every approved
 *  field ticket, grouped customer → job in the API's order. Tick tickets and
 *  press Generate (`{ ticket_ids }`), or "Invoice whole job" (`{ job_id }`). A
 *  refusal (TICKETS_DIFFERENT_CUSTOMERS, TAX_UNCONFIGURED, …) is shown in the
 *  API's own sentence — the API decides what can go on one invoice, not this
 *  screen. The signer comes from the approved-ticket list, joined on the
 *  ticket id; the billable read does not carry it.
 *
 *  INVOICES — PT has no list read of its own, so the register is the unified
 *  invoice list's PT branch (`GET /unified-invoices?vertical=pressure_testing`),
 *  one page of 100, newest first. Its `invoice_date` is the row's created_at
 *  (an instant, shown as the tenant's day); its `due_date` is the stored DATE.
 *  The job is `source_id` (the invoice's job_id), named from the PT jobs list;
 *  a multi-job invoice has none and says "Several jobs".
 *
 * A row opens the invoice on its own page, /pressure-testing/invoices/:invoiceId
 * (InvoiceDetailScreen), and a ticket number under Ready to invoice opens the
 * ticket's page; nothing opens beside or over the lists, since split panes and
 * drawers are banned (CLAUDE.md). A new invoice opens on its page as soon as it
 * is generated. A legacy `?invoice=<id>` link is forwarded to the invoice page.
 * The ticket selection is a pick set for one Generate, not a narrowing of the
 * list, so it is kept in component state and not in the URL.
 *
 * Every figure is the API's: ticket subtotals, the job's subtotal (the API's
 * sum), invoice totals. The band counts are tallies of the loaded lists — the
 * billable read is unpaginated, and the invoice counts are shown only while the
 * page holds every invoice the API reports.
 */
import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ClipboardCheck, FileClock, Loader2, Receipt, Send } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useGeneratePtInvoice, usePtBillable, usePtInvoices, usePtJobs, usePtTickets } from '../../hooks/usePressureTesting';
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWritePtInvoices, ptInvoiceStatusSkin } from '../../lib/pressure-testing';
import type { PtBillableJob, PtBillableTicket, PtInvoiceGenerate } from '../../types/pressure-testing';
import { PT_SECONDARY, PtEmpty, PtPanel, PtSkinBadge } from './PtBits';
import { ptWriteError } from './PtJobDialogs';
import { useOpenRecord } from '../downhole/recordPage';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';

/** A billable ticket's day: its work day, else what it covers. */
function billableDay(t: PtBillableTicket): string {
  if (t.work_date) return formatCalendarDate(t.work_date) ?? '—';
  if (t.scope === 'final') return 'Final ticket';
  if (t.scope === 'job') return 'Whole job';
  return '—';
}

/** The billable jobs under the customer each belongs to, in the API's order. */
export function groupBillableByCustomer(jobs: PtBillableJob[]) {
  const groups: { customerId: string; customerName: string | null; jobs: PtBillableJob[] }[] = [];
  for (const j of jobs) {
    let g = groups.find((x) => x.customerId === j.customer_id);
    if (!g) {
      g = { customerId: j.customer_id, customerName: j.customer_name, jobs: [] };
      groups.push(g);
    }
    g.jobs.push(j);
  }
  return groups;
}

export default function PtInvoicesScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tz = useTenantTimeZone();
  const canWrite = canWritePtInvoices(user?.role);
  const [searchParams] = useSearchParams();
  const openRecord = useOpenRecord();
  const legacyInvoice = searchParams.get('invoice');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refusal, setRefusal] = useState<string | null>(null);

  const billable = usePtBillable();
  const invoices = usePtInvoices();
  const approved = usePtTickets({ status: 'approved' });
  const jobs = usePtJobs();
  const generate = useGeneratePtInvoice();

  const billableFailure = queryLoadFailure(billable);
  const invoiceFailure = queryLoadFailure(invoices);
  const billableJobs = useMemo(() => billable.data?.data ?? [], [billable.data]);
  const invoiceRows = invoices.data?.rows ?? [];
  const invoiceTotal = invoices.data?.total ?? null;
  const groups = useMemo(() => groupBillableByCustomer(billableJobs), [billableJobs]);

  const signer = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of approved.data?.data ?? []) m.set(t.id, t.signature?.signer_name ?? null);
    return m;
  }, [approved.data]);
  const jobNumber = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of jobs.data?.data ?? []) m.set(j.id, j.job_number);
    return m;
  }, [jobs.data]);

  // A ticket that left the billable list (invoiced elsewhere, voided) leaves the selection too.
  const billableIds = useMemo(() => new Set(billableJobs.flatMap((j) => j.tickets.map((t) => t.ticket_id))), [billableJobs]);
  const picked = Array.from(selected).filter((id) => billableIds.has(id));

  const billableLoading = billable.isLoading || !!billableFailure;
  const ticketCount = billableLoading ? undefined : billableJobs.reduce((n, j) => n + j.tickets.length, 0);
  // Counts of the invoice page are honest only while it holds every invoice.
  const invoicesComplete = !invoices.isLoading && !invoiceFailure && invoiceTotal !== null && invoiceTotal <= invoiceRows.length;
  const invoiceTally = (status: string) => (invoicesComplete ? invoiceRows.filter((r) => r.status === status).length : undefined);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = (body: PtInvoiceGenerate) => {
    setRefusal(null);
    generate.mutate(body, {
      onSuccess: (res) => {
        const inv = res.data;
        setSelected(new Set());
        toast.success(inv ? `${inv.invoice_number} created` : 'Invoice created');
        if (inv) openInvoice(inv.id);
      },
      onError: (err) => setRefusal(ptWriteError(err, 'The invoice was not created.').message),
    });
  };

  const openInvoice = (id: string) => openRecord(`/pressure-testing/invoices/${id}`);
  const openTicket = (id: string) => openRecord(`/pressure-testing/tickets/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openInvoice(id);
    }
  };
  const jobLabel = (sourceId: string | null) => (sourceId ? jobNumber.get(sourceId) ?? '—' : 'Several jobs');

  if (legacyInvoice) return <Navigate to={`/pressure-testing/invoices/${legacyInvoice}`} replace />;

  return (
    <div data-testid="pt-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          // Finance → Invoices is the one list across verticals; this screen
          // keeps the Ready-to-invoice queue and the generate flow.
          <Link to="/finance/invoices?vertical=pressure_testing" className="fi-action fi-action--standard fi-action--secondary" data-testid="pt-all-invoices-link">
            All invoices
          </Link>
        }
        stats={[
          {
            icon: <ClipboardCheck className="size-4" aria-hidden="true" />,
            label: 'Ready',
            sub: 'Jobs to invoice',
            tone: 'accent',
            loading: billable.isLoading,
            value: displayValue(billableLoading ? undefined : billableJobs.length),
          },
          {
            icon: <Receipt className="size-4" aria-hidden="true" />,
            label: 'Tickets',
            sub: 'Approved, unbilled',
            tone: 'accent',
            loading: billable.isLoading,
            value: displayValue(ticketCount),
          },
          {
            icon: <FileClock className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            sub: 'Not yet sent',
            tone: 'accent',
            loading: invoices.isLoading,
            value: displayValue(invoiceTally('draft')),
          },
          {
            icon: <Send className="size-4" aria-hidden="true" />,
            label: 'Sent',
            sub: 'With the customer',
            tone: 'info',
            loading: invoices.isLoading,
            value: displayValue(invoiceTally('sent')),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PtPanel
            eyebrow="Approved field tickets"
            title="Ready to invoice"
            testId="pt-billable"
            actions={
              canWrite && billableJobs.length > 0 ? (
                <button type="button" className={PRIMARY} disabled={picked.length === 0 || generate.isPending} onClick={() => run({ ticket_ids: picked })} data-testid="pt-generate-selected">
                  {generate.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Receipt className="size-4" aria-hidden="true" />}
                  {picked.length ? `Generate invoice (${picked.length} ticket${picked.length === 1 ? '' : 's'})` : 'Generate invoice'}
                </button>
              ) : undefined
            }
            footer={!canWrite && billableJobs.length > 0 ? 'Only an admin or office staff can generate an invoice.' : undefined}
          >
            {refusal && (
              <p role="alert" data-testid="pt-generate-refusal" className="mx-4 mt-4 rounded-fi-control border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                {refusal}
              </p>
            )}
            {billable.isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading tickets ready to invoice" />
              </div>
            ) : billableFailure ? (
              <LoadFailureNotice error={queryFailureError(billable)} kind={billableFailure} subject="tickets ready to invoice" />
            ) : billableJobs.length === 0 ? (
              <PtEmpty>No approved field tickets are waiting to be invoiced.</PtEmpty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    <tr className="border-l-4 border-l-transparent">
                      <th scope="col" className="w-[44px] px-4 py-3"><span className="sr-only">Select</span></th>
                      <th scope="col" className="px-4 py-3">Ticket #</th>
                      <th scope="col" className="px-4 py-3">Work date</th>
                      <th scope="col" className="px-4 py-3">Signed by</th>
                      <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  {groups.map((g) => (
                    <tbody key={g.customerId} data-testid="pt-billable-customer">
                      <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
                        <th scope="colgroup" colSpan={5} className="px-4 py-2 text-left text-sm font-extrabold text-white">
                          {g.customerName || '—'}
                        </th>
                      </tr>
                      {g.jobs.map((j) => (
                        <React.Fragment key={j.job_id}>
                          <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent" data-testid="pt-billable-job">
                            <td colSpan={4} className="px-4 py-2">
                              <span className="font-mono text-sm font-bold text-fieldiq-text">{j.job_number || '—'}</span>
                              <span className="ml-2 text-xs text-fieldiq-muted">
                                {j.province_state || 'Province not set'} · {j.tickets.length} ticket{j.tickets.length === 1 ? '' : 's'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <span className="font-mono text-sm tabular-nums text-fieldiq-text" data-testid="pt-billable-job-subtotal">
                                  {fmtCurrency(j.subtotal, j.currency)}
                                </span>
                                {canWrite && (
                                  <button
                                    type="button"
                                    className={PT_SECONDARY}
                                    disabled={generate.isPending}
                                    onClick={() => run({ job_id: j.job_id })}
                                    data-testid="pt-generate-job"
                                  >
                                    Invoice whole job
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {j.tickets.map((t) => (
                            <tr key={t.ticket_id} className="border-t border-l-4 border-fieldiq-border border-l-fi-green" data-testid="pt-billable-ticket">
                              <td className="px-4 py-2.5">
                                {canWrite && (
                                  <input
                                    type="checkbox"
                                    className="size-4 accent-fi-amber"
                                    checked={selected.has(t.ticket_id)}
                                    onChange={() => toggle(t.ticket_id)}
                                    aria-label={`Select ${t.ticket_number}`}
                                    data-testid="pt-billable-select"
                                  />
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                <button
                                  type="button"
                                  className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline"
                                  onClick={() => openTicket(t.ticket_id)}
                                >
                                  {t.ticket_number || '—'}
                                </button>
                              </td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{billableDay(t)}</td>
                              <td className="px-4 py-2.5 text-sm text-fieldiq-text" data-testid="pt-billable-signer">
                                {signer.get(t.ticket_id) || '—'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(t.subtotal, t.currency)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            )}
          </PtPanel>

          <PtPanel
            eyebrow="Billed from field tickets"
            title="Invoices"
            testId="pt-invoice-register"
            footer={
              !invoices.isLoading && !invoiceFailure && invoiceRows.length > 0
                ? invoiceTotal !== null && invoiceTotal > invoiceRows.length
                  ? `Showing the newest ${invoiceRows.length} of ${invoiceTotal} invoices`
                  : `${invoiceRows.length} invoice${invoiceRows.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            {invoices.isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading invoices" />
              </div>
            ) : invoiceFailure ? (
              <LoadFailureNotice error={queryFailureError(invoices)} kind={invoiceFailure} subject="invoices" />
            ) : invoiceRows.length === 0 ? (
              <PtEmpty>There are no pressure-testing invoices yet.</PtEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[880px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Invoice #</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Job</th>
                        <th scope="col" className="px-4 py-3">Invoice date</th>
                        <th scope="col" className="px-4 py-3">Due</th>
                        <th scope="col" className="px-4 py-3 text-right">Total</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceRows.map((r) => (
                        <tr
                          key={r.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openInvoice(r.id)}
                          onKeyDown={rowKeys(r.id)}
                          data-testid="pt-invoice-row"
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${ptInvoiceStatusSkin(r.status).rail}`}
                        >
                          <td className="px-4 py-3 font-mono text-sm font-extrabold text-white">{r.invoice_number || '—'}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-white">{r.customer_name || '—'}</td>
                          <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{jobLabel(r.source_id)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantTenantDay(r.invoice_date, tz) ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatCalendarDate(r.due_date) ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white" data-testid="pt-invoice-row-total">
                            {fmtCurrency(r.total, r.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <PtSkinBadge skin={ptInvoiceStatusSkin(r.status)} testId="pt-invoice-row-status" value={r.status} />
                          </td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {invoiceRows.map((r) => (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openInvoice(r.id)}
                      onKeyDown={rowKeys(r.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-extrabold text-white">{r.invoice_number || '—'}</p>
                          <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                            {jobLabel(r.source_id)} · {r.customer_name || '—'}
                          </p>
                        </div>
                        <PtSkinBadge skin={ptInvoiceStatusSkin(r.status)} />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                        <span>Due {formatCalendarDate(r.due_date) ?? '—'}</span>
                        <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </PtPanel>
        </div>
      </section>
    </div>
  );
}
